import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Stethoscope, ArrowLeft, TrendingUp, Clock, Users } from 'lucide-react';

const HealthcareCaseStudy = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                <Stethoscope className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="text-sm text-[#2563EB] font-semibold uppercase tracking-wide">
                  Healthcare
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC]">
                  Bright Smile Dental Group
                </h1>
              </div>
            </div>
            <p className="text-xl text-[#94A3B8]">
              How a dental practice group increased treatment acceptance by 38% with competitive cash-pay pricing
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1200"
              alt="Bright Smile Dental Group"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Users className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">4 Locations</div>
              <div className="text-sm text-[#94A3B8]">Regional provider</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">$2.1M</div>
              <div className="text-sm text-[#94A3B8]">Annual savings achieved</div>
            </div>
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-6">
              <Clock className="w-8 h-8 text-[#2563EB] mb-3" />
              <div className="text-2xl font-bold text-[#F8FAFC] mb-1">30%</div>
              <div className="text-sm text-[#94A3B8]">Faster billing cycles</div>
            </div>
          </div>

          <div className="prose prose-invert prose-lg max-w-none">
            <div className="text-[#94A3B8] leading-relaxed space-y-8">
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-red-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Challenge</h2>
                <p className="text-lg">
                  CareFirst Medical Group faced complex reimbursement models and outdated fee schedules causing over $2M in lost revenue annually. Their 30+ day billing cycles created significant cash flow issues, while manual compliance checking was both time consuming and error prone.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-[#94A3B8] mt-4">
                  <li>Fee schedules not optimized for current reimbursement rates</li>
                  <li>Manual billing process taking 30+ days</li>
                  <li>Compliance errors resulting in claim denials</li>
                  <li>No systematic approach to patient cohort pricing</li>
                  <li>Revenue leakage from outdated pricing models</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">The Solution</h2>
                <p className="text-lg">
                  PriceStrike deployed patient cohort pricing optimized for outcomes and reimbursement rates, with automated compliance checking and real time rate updates integrated directly with their billing systems.
                </p>
                <p className="mt-4">
                  The platform analyzes historical reimbursement data, identifies optimal pricing for different patient cohorts, and automatically updates fee schedules based on payer contract changes. Compliance rules are built in, reducing claim denials by 85%.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-green-500/30 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-[#F8FAFC] mb-6">The Results</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">$2.1M</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Annual Savings</div>
                    <div className="text-sm text-[#94A3B8]">Cost reductions achieved</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">30%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Faster Billing</div>
                    <div className="text-sm text-[#94A3B8]">Billing cycle improvement</div>
                  </div>
                  <div className="text-center p-6 bg-[#0A0F1C]/50 rounded-xl border border-[#2563EB]/20">
                    <div className="text-5xl font-bold text-[#2563EB] mb-2">95%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Compliance Rate</div>
                    <div className="text-sm text-[#94A3B8]">Improved accuracy</div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent border border-[#2563EB]/30 rounded-xl p-8">
                <div className="text-4xl text-[#2563EB] mb-4">"</div>
                <p className="text-[#F8FAFC] text-2xl leading-relaxed mb-6 italic">
                  PriceStrike transformed our pricing from reactive to strategic. We're now maximizing reimbursements while maintaining patient satisfaction. The cash flow improvement alone justified the investment.
                </p>
                <div className="border-t border-[#2563EB]/20 pt-4">
                  <div className="font-semibold text-[#F8FAFC] text-lg">Dr. Michael Torres</div>
                  <div className="text-[#94A3B8]">Chief Financial Officer</div>
                  <div className="text-sm text-[#2563EB] mt-1">CareFirst Medical Group</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 p-8 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl text-center">
            <h3 className="text-3xl font-bold text-[#F8FAFC] mb-4">
              Transform Your Healthcare Pricing
            </h3>
            <p className="text-[#94A3B8] mb-6 text-lg">
              Optimize reimbursements and accelerate billing with PriceStrike.
            </p>
            <Link to="/pricing" className="inline-block bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg">
              Explore Pricing Plans
            </Link>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default HealthcareCaseStudy;
